import { LitElement, html, css, nothing } from 'lit';

export class KatapultIcon extends LitElement {
  static properties = {
    _animate: { state: true },
    color: { type: String },
    config: { type: Object },
    fill: { type: Boolean },
    _fill: { state: true },
    outline: { type: Boolean },
    grade: { type: String },
    opticalSize: { type: String },
    _opticalSize: { state: true },
    icon: { type: String },
    inferType: { type: Boolean },
    _render: { state: true },
    size: { type: String },
    _size: { state: true },
    type: { type: String },
    _type: { state: true },
    weight: { type: String }
  };

  static styles = css`
    :host {
      position: relative;
      display: block;
      color: var(--katapult-icon-color);
      font-size: var(--katapult-icon-size);
      height: var(--katapult-icon-size);
      width: var(--katapult-icon-size);
      transition: var(--katapult-icon-transition);
    }

    /* Iron Icon */
    iron-icon {
      position: absolute;
      height: inherit;
      width: inherit;
      pointer-events: none;
      top: 0;
      left: 0;
    }

    /* Material Icon & Symbol */
    .material-symbol {
      position: absolute;
      top: 0;
      left: 0;
      height: inherit;
      width: inherit;
      font-family: var(--katapult-icon-font-family);
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-feature-settings: 'liga';
      -webkit-font-smoothing: antialiased;
      font-variation-settings: var(--katapult-icon-font-variation-settings);
      transition: font-variation-settings 0.3s;
      user-select: none;
      -webkit-user-select: none; /* Safari */
      pointer-events: none;
    }
  `;

  static calcSize(size) {
    return { small: '20', normal: '24', large: '40', 'extra-large': '48' }[size] ?? size ?? '24';
  }

  render() {
    return this._iconTemplate();
  }

  _iconTemplate() {
    if (this._render) {
      switch (this._type) {
        case 'iron-icon':
          return html`<iron-icon icon="${this.icon}"></iron-icon>`;
        case 'material-icon':
        case 'material-symbol':
          return html`<span class="material-symbol">${this.icon}</span>`;
      }
    }
    return nothing;
  }

  constructor() {
    super();
    // Set default properties.
    this._render = true;
    this._animate = false;
  }

  async willUpdate(changedProperties) {
    // Update properties from config.
    if (changedProperties.has('config')) {
      // Allow config object to set any public property.
      for (const prop in KatapultIcon.properties) {
        // Skip any private properties.
        if (KatapultIcon.properties[prop].state || prop == 'config') continue;
        if (this.config?.[prop] != null || changedProperties.get('config')?.[prop] != null) this[prop] = this.config[prop];
      }
    }

    // Update computed internal properties.
    this._size = KatapultIcon.calcSize(this.size);
    if (['_type', 'type', 'inferType', 'icon'].some((x) => changedProperties.has(x)))
      this._type = this.type ?? (this.inferType ? await this._inferIconType(this.icon) : 'material-symbol');
    this._fill = this.fill ?? !this.outline;
    this.weight ??= '400';
    this.grade ??= '0';
    this._opticalSize = this.opticalSize ?? this._size;

    // Load iron-icon elements if needed.
    if (changedProperties.has('_type') && this._type == 'iron-icon') this._loadIronIcons();

    // Update font variation settings.
    if (['_fill', 'weight', 'grade', '_opticalSize'].some((x) => changedProperties.has(x))) {
      this.style.setProperty(
        '--katapult-icon-font-variation-settings',
        `'FILL' ${this._fill ? '1' : '0'}, 'wght' ${this.weight}, 'GRAD' ${this.grade}, 'opsz' ${this._opticalSize}`
      );
    }

    // Update icon size.
    if (changedProperties.has('_size')) {
      this.style.setProperty('--katapult-icon-size', `${this._size}px`);
    }

    // Update icon color.
    if (changedProperties.has('color')) {
      this.style.setProperty('--katapult-icon-color', `${this.color}`);
    }

    // Update font family.
    if (changedProperties.has('_type')) {
      // See if this icon type requires a font.
      const font = {
        'material-icon': 'Material Icons',
        'material-symbol': 'Material Symbols Rounded'
      }[this._type];
      // Update font family.
      this.style.setProperty('--katapult-icon-font-family', font);
      // If there is a font, wait for it to be loaded.
      if (font) {
        // Clear render flag.
        this._render = false;
        // Load the font face.
        const loaded = await document.fonts.load(`0px ${font}`).then((x) => x[0] != null);
        // If the font face is loaded, set the render flag again.
        if (loaded) this._render = true;
      }
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('_animate')) {
      this.style.setProperty(
        '--katapult-icon-transition',
        this._animate ? 'font-size 0.3s, color 0.3s, height 0.3s, width 0.3s, opacity 0.3s, transform 0.3s' : ''
      );
    }

    if (!this._animate) this._animate = true;
  }

  /**
   * An experimental function that attempts to determine whether an icon is an iron icon or a material symbol based upon name alone.
   * @param {String} icon
   * @returns {Boolean}
   */
  async _inferIconType(icon) {
    if (!icon) return;
    // Only material symbols include underscores.
    if (icon.includes('_')) return 'material-symbol';

    // Only iron icons include dashes and colons.
    if (icon.includes('-') || icon.includes(':')) return 'iron-icon';

    // See if symbol svg for this icon can be loaded (abort after a few seconds).
    const controller = new AbortController();
    let timedOut = false;
    const abortTimeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 3000);
    const symbolExists = await fetch(`https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsrounded/${icon}/fill1/20px.svg`)
      .then((res) => {
        clearTimeout(abortTimeout);
        return (res) => res.ok;
      })
      .catch((err) => false);
    if (symbolExists) return 'material-symbol';

    // See if icon exists in iron icon set.
    const IronMeta = await import('@polymer/iron-meta/iron-meta.js').then((x) => x.IronMeta);
    this._ironMeta ??= new IronMeta({ type: 'iconset' });
    const ironIconExists = this._ironMeta.byKey('icons')?.applyIcon(document.createElement('text'), icon) != null;
    if (ironIconExists) return 'iron-icon';

    // If we timed out earlier, assume this is a material symbol.
    if (timedOut) return 'material-symbol';

    // If we didn't time out earlier, we know that this isn't a material symbol, so return nothing.
    return;
  }

  _loadIronIcons() {
    if (window._katapultIconLoadedIronIcons) return;
    window._katapultIconLoadedIronIcons ||= true;
    import('@polymer/iron-icon/iron-icon.js');
    import('@polymer/iron-icon/iron-icon.js');
    import('@polymer/iron-icons/iron-icons.js');
    import('@polymer/iron-icons/maps-icons.js');
    import('@polymer/iron-icons/av-icons.js');
    import('@polymer/iron-icons/social-icons.js');
    import('@polymer/iron-icons/device-icons.js');
    import('@polymer/iron-icons/editor-icons.js');
    import('@polymer/iron-icons/communication-icons.js');
    import('@polymer/iron-icons/hardware-icons.js');
    import('@polymer/iron-icons/image-icons.js');
  }
}

customElements.define('katapult-icon', KatapultIcon);
